import React, { useState, useContext, MouseEvent, useEffect, useRef } from 'react';
import {
  createStyles,
  makeStyles,
  Theme,
  IconButton,
  Drawer,
  Button,
} from '@material-ui/core';
import TextField from '@cbreone/core-ui/dist/components/DebouncedTextField';
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from 'react-i18next';
import { CommCard } from './CommCard';
import CommentsContext from '../../hooks/CommentsContext';
import InteractiveSurveyContext from '../../hooks/InteractiveSurveyContext';
import { Comment, CommentArray } from '../../types';

const useStyles = makeStyles((theme: Theme) => createStyles({
  commentsPanel: {
    height: '100vh',
    width: '360px',
    zIndex: theme.zIndex.drawer + 1,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    width: '100%',
    padding: theme.spacing(2),
    borderBottom: '1px solid #ccc',
    backgroundColor: 'white',
    '& h2': {
      margin: '0 0 8px 0',
    },
    '& p': {
      margin: '0px',
      color: '#003F2D',
    },
  },
  commentLog: {
    margin: '0',
    flex: 1,
    overflowX: 'auto',
    display: 'flex',
    flexDirection: 'column',
    '& p': {
      margin: 'auto',
    },
  },
  commentBox: {
    width: '360px',
    boxShadow: '1px 0px 5px 0px rgba(0, 0, 0, 0.14)',
    padding: '20px',
  },
  closer: {
    position: 'fixed',
    top: '0px',
    right: '0px',
    margin: '6px',
  },
  noComms: {
    width: '100%',
    height: '100%',
    textAlign: 'center',
  },
  disabledSubmitButton: {
    height: '30px',
    boxShadow: 'none',
    '& .MuiButton-label': {
      textTransform: 'none',
    },
    '&.MuiButtonBase-root:hover': {
      boxShadow: 'none',
    },
  },
  enabledSubmitButton: {
    height: '30px',
    boxShadow: 'none',
    backgroundColor: '#003F2D',
    '& .MuiButton-label': {
      textTransform: 'none',
      color: '#FFFFFF',
      padding: '2px, 0px, 2px, 0px',
      size: '16px',
      weight: '500',
      font: 'Calibre-R',
      lineHieght: '16px',
    },
    '&.MuiButtonBase-root:hover': {
      boxShadow: 'none',
      backgroundColor: '#003F2D',
    },
  },
}));

export interface UndoText {
  comment: string;
  isNew: boolean;
  propertyId: string;
  surveyId: string;
  userName: string;
}

type Props = {
  onDismiss: () => void;
  commentArray: CommentArray;
  editSurveyComments: (propId?: string | undefined, newComment?: string | undefined) => void;
  returnUndoText: UndoText;
  onClose: () => void;
};

const CommentsPanel: React.FC<Props> = ({
  onDismiss,
  commentArray,
  editSurveyComments,
  returnUndoText,
  onClose,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [newComment, setNewComment] = useState('');
  const {
    commentPanelStatus,
    commPropId,
    setUser,
    setCommentPending,
    commentPending,
  } = useContext(CommentsContext);
  const { survey } = useContext(InteractiveSurveyContext);
  let userName = localStorage.getItem('displayName');
  const [isDisabled, setIsDisabled] = useState(true);
  const [commentsRender, setCommentsRender] = useState<number>(0);
  const listRef = useRef<null | HTMLDivElement>(null);

  const orderCommentsByDate = () => commentArray?.map((item: any) => {
    const convertUTCToNumber = new Date(item.createdOn).getTime();
    return { ...item, convertUTCToNumber };
  }).sort((a: any, b: any) => a.convertUTCToNumber - b.convertUTCToNumber);

  const includeNewDate = orderCommentsByDate()?.filter((comm: Comment) => comm.propertyId === commPropId).map((comment: Comment) => comment.isNew);

  const onSubmit = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setCommentPending(true);
    editSurveyComments(commPropId, newComment);
    setNewComment('');
    setIsDisabled(true);
    setTimeout(() => listRef?.current?.lastElementChild?.scrollIntoView({ behavior: 'smooth' }), 0);
  };

  useEffect(() => {
    if (!commentPanelStatus) {
      localStorage.setItem(commPropId, newComment);
      setNewComment('');
    } else if (commentPanelStatus) {
      const draft = localStorage.getItem(commPropId);
      if (draft != null) {
        setNewComment(draft);
      }
    }
  }, [commentPanelStatus, commPropId]);

  const validateForm = (commStr: string) => {
    scrollSubmitButtonIntoView();
    if (!userName) return false;
    if (/\w/.test(commStr)) {
      setIsDisabled(false);
      return true;
    } 
    setIsDisabled(true);
    return false;
  };

  useEffect(() => {
    validateForm(newComment);
    if (userName === '') {
      setIsDisabled(true);
    }
    if (commentPending === true) {
      setIsDisabled(true);
    }
    if (includeNewDate?.includes(false)) {
      setCommentsRender(Math.random());
    }
  }, [newComment, userName, commentPending]);

  useEffect(() => {
    if (returnUndoText) {
      setNewComment(returnUndoText.comment);
    }
  }, [returnUndoText]);

  const setClientUser = (user: string) => {
    localStorage.setItem('displayName', user);
    setUser(user);
    userName = user;
    validateForm(newComment);
  };

  const scrollSubmitButtonIntoView = () => {
    const element = document.getElementById('commentSubmitButton');
    return element?.scrollIntoView({ block: 'center' });
  };

  return (
    <Drawer
      anchor='right'
      open={commentPanelStatus}
      variant='temporary'
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          onDismiss();
          return;
        }
        onClose();
      }}
      >
      <div className={classes.commentsPanel}>
        <div className={classes.header}>
          <h2>{t('comments.title')}</h2>
          <IconButton
            id="SC-CloseManageProperties"
            className={classes.closer}
            onClick={onDismiss}
          >
            <CloseIcon />
          </IconButton>
          {survey?.properties?.filter((prop: any) => prop.id === commPropId).map(
            (item: any) =>
              <p key={item.id}>{t('comments.propertyAddress')}: {item.address1}</p>,
          )}
        </div>
        <div className={classes.commentLog} ref={listRef}>
          { orderCommentsByDate()?.filter((comm: Comment) => comm.propertyId === commPropId).length
            ? orderCommentsByDate().filter((comm: Comment) => comm.propertyId === commPropId).map((comment: Comment, index: number) => (
              <CommCard
                key={comment.commentId || comment.createdOn || `${comment.propertyId}-${index}`}
                comm={comment}
                editSurveyComments={editSurveyComments}
              />))
            : (<p>{t('comments.leaveComment')}</p>)
          }
        </div>
        <div className={classes.commentBox}>
          <TextField
            fullWidth
            name='name'
            label={t('comments.nameRequired')}
            variant='outlined'
            margin='dense'
            className='full'
            value={userName}
            onBlur={(e) => setClientUser(e.target.value)}
          />
          <TextField
            multiline
            minRows={7}
            fullWidth
            name="comments"
            label={t('comments.enterComments')}
            variant='outlined'
            margin='dense'
            className='full'
            value={newComment}
            onKeyUp={(e) => validateForm((e.target as HTMLInputElement).value)}
            onBlur={(e) => setNewComment((e.target as HTMLInputElement).value)}
          />
          <div style={{ textAlign: 'right', paddingTop: '16px' }}>
            <Button
              id="commentSubmitButton"
              className={isDisabled ? classes.disabledSubmitButton : classes.enabledSubmitButton}
              variant="contained"
              disabled={isDisabled}
              onClick={(e) => onSubmit(e)}
            >
              {t('comments.submit')}
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

CommentsPanel.displayName = 'Comments';
export default CommentsPanel;
